// frontend/src/pages/OrderHistoryPage.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ordersApi } from '../api/orders';
import helpers from '../utils/helpers';
import { 
    FaBox, FaCalendarAlt, FaArrowLeft, FaCircle, FaCheckCircle, 
    FaTruck, FaSpinner, FaArchive, FaHistory, FaCheckSquare, FaSquare,
    FaTimesCircle, FaBan
} from 'react-icons/fa';

function OrderHistoryPage() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confirmingId, setConfirmingId] = useState(null);
  const [cancellingId, setCancellingId] = useState(null);
  const [activeTab, setActiveTab] = useState('active');
  const [selectedIds, setSelectedIds] = useState([]);
  const [isBulkProcessing, setIsBulkProcessing] = useState(false);

  useEffect(() => { fetchOrders(); }, []);
  useEffect(() => { setSelectedIds([]); }, [activeTab]);

  const fetchOrders = async () => {
    try {
      const data = await ordersApi.getUserOrders();
      setOrders(data);
    } catch (err) {
      console.error("Error fetching orders:", err);
      setError("Failed to load your order history. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const displayedOrders = orders.filter(o =>
    activeTab === 'active' ? !o.is_archived : o.is_archived
  );

  const handleSelectAll = () => {
    if (selectedIds.length === displayedOrders.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(displayedOrders.map(o => o.id));
    }
  };

  const handleSelectOne = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  const handleBulkArchive = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Archive ${selectedIds.length} orders?`)) return;
    setIsBulkProcessing(true);
    try {
      await Promise.all(selectedIds.map(id => ordersApi.archiveOrder(id)));
      setOrders(prev => prev.map(o => selectedIds.includes(o.id) ? { ...o, is_archived: true } : o));
      setSelectedIds([]);
    } catch (err) {
      alert("Failed to archive some orders.");
    } finally {
      setIsBulkProcessing(false);
    }
  };

  const handleConfirmReceipt = async (orderId) => {
    if (!window.confirm("Have you received and inspected this item? Confirming will release funds to the seller.")) return;
    setConfirmingId(orderId);
    try {
      await ordersApi.confirmDelivery(orderId);
      alert("Receipt Confirmed! Thank you for shopping with us.");
      fetchOrders();
    } catch (err) {
      alert("Failed to confirm receipt.");
    } finally {
      setConfirmingId(null);
    }
  };

  const handleArchive = async (orderId) => {
    if (!window.confirm("Archive this order?")) return;
    try {
      await ordersApi.archiveOrder(orderId);
      setOrders(prev => prev.map(o => o.id === orderId ? { ...o, is_archived: true } : o));
    } catch (err) {
      alert("Failed to archive order.");
    }
  };

  const handleCancelOrder = async (orderId, orderNumber) => {
    if (!window.confirm(`Are you sure you want to cancel order ${orderNumber}? This action cannot be undone.`)) return;
    setCancellingId(orderId);
    try {
      await ordersApi.cancelOrder(orderId);
      setOrders(prev => prev.map(o => o.id === orderId ? { ...o, status: 'cancelled' } : o));
      alert(`Order ${orderNumber} has been successfully cancelled. If you paid online, your refund will be processed within 5-7 business days.`);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to cancel order.';
      alert(msg);
    } finally {
      setCancellingId(null);
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'delivered': return 'text-green-600 bg-green-100';
      case 'shipped': return 'text-blue-600 bg-blue-100';
      case 'processing': return 'text-yellow-600 bg-yellow-100';
      case 'cancelled': return 'text-red-600 bg-red-100';
      case 'confirmed': return 'text-indigo-600 bg-indigo-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const canCancel = (status) => {
    return ['pending', 'confirmed', 'processing'].includes(status?.toLowerCase());
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen py-12">
      <div className="container mx-auto px-4 max-w-5xl">

        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold text-gray-800">My Orders</h1>
          <Link to="/account" className="text-primary hover:underline flex items-center gap-2">
            <FaArrowLeft /> Back to Dashboard
          </Link>
        </div>

        {/* TABS */}
        <div className="flex flex-col md:flex-row justify-between items-center border-b border-gray-200 mb-6 gap-4">
          <div className="flex gap-4">
            <button
              onClick={() => setActiveTab('active')}
              className={`pb-2 px-4 font-medium flex items-center gap-2 ${activeTab === 'active' ? 'border-b-2 border-primary text-primary' : 'text-gray-500'}`}
            >
              <FaBox /> Active Orders
            </button>
            <button
              onClick={() => setActiveTab('archived')}
              className={`pb-2 px-4 font-medium flex items-center gap-2 ${activeTab === 'archived' ? 'border-b-2 border-primary text-primary' : 'text-gray-500'}`}
            >
              <FaHistory /> Archived
            </button>
          </div>

          {displayedOrders.length > 0 && activeTab === 'active' && (
            <button
              onClick={handleSelectAll}
              className="flex items-center gap-2 text-sm font-bold text-gray-600 hover:text-primary mb-2 md:mb-0"
            >
              {selectedIds.length === displayedOrders.length ? <FaCheckSquare className="text-primary" /> : <FaSquare />}
              Select All
            </button>
          )}
        </div>

        {/* BULK ACTION BAR */}
        {selectedIds.length > 0 && activeTab === 'active' && (
          <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg mb-6 flex justify-between items-center">
            <span className="font-bold text-blue-800 text-sm">{selectedIds.length} orders selected</span>
            <button
              onClick={handleBulkArchive}
              disabled={isBulkProcessing}
              className="bg-gray-600 text-white px-4 py-2 rounded text-sm font-bold hover:bg-gray-700 flex items-center gap-2"
            >
              {isBulkProcessing ? <FaSpinner className="animate-spin" /> : <FaArchive />}
              Archive Selected
            </button>
          </div>
        )}

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">{error}</div>
        )}

        {displayedOrders.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <div className="bg-gray-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
              <FaBox className="text-gray-400 text-3xl" />
            </div>
            <h2 className="text-xl font-semibold text-gray-800 mb-2">No {activeTab} orders</h2>
            {activeTab === 'active' && (
              <Link to="/parts" className="bg-primary hover:bg-primary-dark text-white font-bold py-3 px-8 rounded-lg transition duration-300 inline-block mt-4">
                Start Shopping
              </Link>
            )}
          </div>
        ) : (
          <div className="space-y-6">
            {displayedOrders.map((order) => (
              <div key={order.id} className={`bg-white rounded-lg shadow-sm overflow-hidden border transition-all ${selectedIds.includes(order.id) ? 'border-blue-500 ring-1 ring-blue-500' : 'border-gray-200'}`}>

                {/* Order Header */}
                <div className="bg-gray-50 px-4 py-4 border-b border-gray-200 flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    {activeTab === 'active' && (
                      <button onClick={() => handleSelectOne(order.id)} className="text-lg">
                        {selectedIds.includes(order.id) ? <FaCheckSquare className="text-primary" /> : <FaSquare className="text-gray-300 hover:text-gray-500" />}
                      </button>
                    )}
                    <div className="flex flex-col md:flex-row gap-2 md:gap-8">
                      <div>
                        <span className="block text-xs text-gray-500 uppercase font-bold">Order Placed</span>
                        <span className="text-sm text-gray-800 flex items-center gap-2">
                          <FaCalendarAlt className="text-gray-400" />
                          {new Date(order.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      <div>
                        <span className="block text-xs text-gray-500 uppercase font-bold">Total</span>
                        <span className="text-sm font-bold text-gray-800">{helpers.formatCurrency(order.total_amount, 'NGN')}</span>
                      </div>
                      <div>
                        <span className="block text-xs text-gray-500 uppercase font-bold">Order #</span>
                        <span className="text-sm text-gray-800 font-mono">{order.order_number}</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 pl-8 md:pl-0 flex-wrap">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                      <FaCircle className="mr-1.5 text-[8px]" />
                      {order.status ? order.status.charAt(0).toUpperCase() + order.status.slice(1) : 'Pending'}
                    </span>

                    {/* CANCEL BUTTON — only for cancellable statuses */}
                    {activeTab === 'active' && canCancel(order.status) && (
                      <button
                        onClick={() => handleCancelOrder(order.id, order.order_number)}
                        disabled={cancellingId === order.id}
                        className="flex items-center gap-1.5 text-xs font-bold text-red-600 border border-red-300 bg-red-50 hover:bg-red-100 px-3 py-1.5 rounded-full transition-colors"
                        title="Cancel this order"
                      >
                        {cancellingId === order.id ? <FaSpinner className="animate-spin" size={10} /> : <FaBan size={10} />}
                        Cancel Order
                      </button>
                    )}

                    {/* ARCHIVE BUTTON */}
                    {activeTab === 'active' && (
                      <button
                        onClick={() => handleArchive(order.id)}
                        className="text-gray-400 hover:text-gray-600 p-2 rounded hover:bg-gray-200"
                        title="Archive Order"
                      >
                        <FaArchive />
                      </button>
                    )}
                  </div>
                </div>

                {/* Order Items */}
                <div className="p-6 pl-12 md:pl-6">
                  <div className="space-y-6">
                    {order.items.map((item, index) => (
                      <div key={index} className="flex items-start gap-4">
                        <div className="w-20 h-20 flex-shrink-0 bg-gray-100 rounded overflow-hidden border border-gray-200">
                          <img
                            src={item.image || 'https://via.placeholder.com/100'}
                            alt={item.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="flex-grow">
                          <h4 className="text-base font-semibold text-gray-800 mb-1">
                            <Link to={`/parts/${item.part_id}`} className="hover:text-primary hover:underline">
                              {item.name}
                            </Link>
                          </h4>
                          <p className="text-sm text-gray-600 mb-2">Quantity: {item.quantity}</p>
                          <p className="text-sm font-medium text-primary">{helpers.formatCurrency(item.price, 'NGN')}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Order Footer */}
                <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex flex-col md:flex-row justify-between items-center gap-4 pl-12 md:pl-6">
                  <div className="text-xs text-gray-500">
                    Payment Method: <span className="font-medium capitalize">{order.payment_method?.replace(/_/g, ' ')}</span>
                  </div>

                  {/* Cancelled notice */}
                  {order.status === 'cancelled' && (
                    <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 px-3 py-2 rounded border border-red-100">
                      <FaTimesCircle />
                      <span>This order was cancelled. {order.payment_method === 'online' ? 'Refund will be processed within 5-7 business days.' : ''}</span>
                    </div>
                  )}

                  {/* Shipped logistics */}
                  {order.status === 'shipped' && (
                    <div className="flex flex-col md:flex-row items-center gap-4 w-full md:w-auto">
                      <div className="text-sm text-blue-800 bg-blue-50 px-3 py-2 rounded border border-blue-100">
                        <p className="font-bold flex items-center gap-2"><FaTruck /> {order.carrier_name}</p>
                        <p className="font-mono text-xs">Waybill: {order.tracking_number}</p>
                        {order.pickup_terminal && <p className="text-xs mt-1"><strong>Pickup:</strong> {order.pickup_terminal}</p>}
                      </div>
                      <button
                        onClick={() => handleConfirmReceipt(order.id)}
                        disabled={confirmingId === order.id}
                        className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-6 rounded shadow-md flex items-center gap-2 transition-transform hover:scale-105"
                      >
                        {confirmingId === order.id ? <FaSpinner className="animate-spin" /> : <FaCheckCircle />}
                        Confirm Receipt
                      </button>
                    </div>
                  )}
                </div>

              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default OrderHistoryPage;